import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../api';

const DEVICE_KEY = 'icecream_device';
const getDevice = () => { try { return localStorage.getItem(DEVICE_KEY); } catch (e) { return null; } };
const when = (utc) => (utc ? new Date(`${utc.replace(' ', 'T')}Z`).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '—');
const browser = (ua) => {
  if (!ua) return '—';
  const os = /iPhone|iPad/.test(ua) ? 'iPhone/iPad' : /Android/.test(ua) ? 'Android' : /Mac OS/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : '';
  const b = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : /Firefox\//.test(ua) ? 'Firefox' : 'Browser';
  return `${b}${os ? ` on ${os}` : ''}`;
};

const S = {
  root: { fontFamily: "'Manrope', system-ui, sans-serif", color: '#231a33', padding: '16px 20px 40px', maxWidth: 1200 },
  h1: { fontFamily: "'Bricolage Grotesque','Manrope',sans-serif", fontSize: 28, margin: '0 0 4px' },
  muted: { color: '#776e86', fontSize: 14, margin: 0 },
  panel: { padding: 16, border: '1px solid #ece6f2', borderRadius: 16, background: '#fff', marginTop: 14 },
  h2: { fontSize: 17, margin: '0 0 10px' },
  row: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
  input: { height: 42, padding: '0 10px', border: '1px solid #e2dcea', borderRadius: 10, font: 'inherit', fontSize: 15, background: '#fff' },
  btn: { height: 42, padding: '0 16px', border: '1px solid #ece6f2', borderRadius: 10, background: '#fff', font: 'inherit', fontSize: 14, fontWeight: 800, cursor: 'pointer' },
  primary: { background: '#5b3fd9', borderColor: '#5b3fd9', color: '#fff' },
  link: { border: 'none', background: 'none', padding: '4px 6px', font: 'inherit', fontSize: 13, fontWeight: 800, color: '#5b3fd9', cursor: 'pointer' },
  wrap: { overflowX: 'auto', border: '1px solid #f0ebf5', borderRadius: 12, marginTop: 10 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: { background: '#faf8fc', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#776e86', padding: '9px 10px', borderBottom: '1px solid #ece6f2', whiteSpace: 'nowrap' },
  td: { padding: '9px 10px', borderBottom: '1px solid #f4f0f8', verticalAlign: 'middle' },
  pill: (ok) => ({ display: 'inline-block', padding: '3px 8px', borderRadius: 999, fontSize: 12, fontWeight: 800, background: ok ? '#e6f6ee' : '#fdecef', color: ok ? '#12905f' : '#d63d5b' }),
  toggle: { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' },
};

export default function SecurityPage() {
  const [settings, setSettings] = useState(null);
  const [devices, setDevices] = useState({ devices: [], this_device_id: null });
  const [logins, setLogins] = useState([]);
  const [name, setName] = useState('Billing PC');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [onlyCashier, setOnlyCashier] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.get('/security/settings'), api.get('/security/devices'), api.get('/security/logins?limit=300')])
      .then(([s, d, l]) => { setSettings(s); setDevices(d); setLogins(l); })
      .catch((e) => setError(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => { setError(''); setBusy(true); try { await fn(); load(); } catch (e) { setError(e.message); } finally { setBusy(false); } };

  const approveThis = () => run(async () => {
    const r = await api.post('/security/devices/approve', { name });
    localStorage.setItem(DEVICE_KEY, r.token);
  });
  const revoke = (d) => {
    if (!window.confirm(`Revoke "${d.name}"? The cashier will no longer be able to use it.`)) return;
    run(async () => {
      await api.post(`/security/devices/${d.id}/revoke`, {});
      if (d.id === devices.this_device_id) localStorage.removeItem(DEVICE_KEY);
    });
  };
  const rename = (d) => {
    const n = window.prompt('New name for this device', d.name);
    if (n && n.trim()) run(() => api.post(`/security/devices/${d.id}/rename`, { name: n.trim() }));
  };
  const saveSettings = (patch) => run(() => api.post('/security/settings', patch));

  const thisApproved = !!devices.this_device_id && !!getDevice();
  const active = devices.devices.filter((d) => !d.revoked);
  const shownLogins = onlyCashier ? logins.filter((l) => l.role && !['owner', 'admin'].includes(String(l.role).toLowerCase())) : logins;

  return (
    <div style={S.root}>
      <h1 style={S.h1}>Security</h1>
      <p style={S.muted}>Controls where and when the cashier account can be used. Your owner login is never restricted.</p>
      {error && <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: '#fdecef', color: '#d63d5b', fontWeight: 700 }}>{error}</div>}

      <section style={S.panel}>
        <h2 style={S.h2}>This device</h2>
        {thisApproved ? (
          <p style={{ margin: 0, fontWeight: 700 }}><span style={S.pill(true)}>Approved</span> &nbsp;This browser can be used by the cashier.</p>
        ) : (
          <>
            <p style={{ ...S.muted, marginBottom: 10 }}>Open this page on the <b>billing PC</b> (or shop phone) while logged in as owner, then approve it. Do this inside the installed Ice Bay POS app if the cashier uses the app.</p>
            <div style={S.row}>
              <input style={{ ...S.input, flex: '1 1 220px' }} value={name} onChange={(e) => setName(e.target.value)} placeholder="Device name, e.g. Billing PC" />
              <button type="button" style={{ ...S.btn, ...S.primary }} disabled={busy || !name.trim()} onClick={approveThis}>Approve this device</button>
            </div>
          </>
        )}
      </section>

      <section style={S.panel}>
        <h2 style={S.h2}>Cashier rules</h2>
        {settings && (
          <div style={{ display: 'grid', gap: 12 }}>
            <label style={S.toggle}>
              <input type="checkbox" checked={settings.devices_on} onChange={(e) => saveSettings({ devices_on: e.target.checked })} />
              Cashier can only use approved devices
            </label>
            {settings.devices_on && !settings.rule_live && (
              <p style={{ ...S.muted, color: '#b86a00', fontWeight: 700 }}>No device approved yet, so this isn't active. Approve the billing PC to switch it on.</p>
            )}
            {settings.devices_on && settings.rule_live && settings.active_devices === 0 && (
              <p style={{ ...S.muted, color: '#d63d5b', fontWeight: 700 }}>Every device is revoked, so the cashier can't log in anywhere. Approve a device to allow billing.</p>
            )}
            <label style={S.toggle}>
              <input type="checkbox" checked={settings.hours_on} onChange={(e) => saveSettings({ hours_on: e.target.checked })} />
              Cashier can only log in during shop hours
            </label>
            {settings.hours_on && (
              <div style={S.row}>
                <span style={S.muted}>From</span>
                <input type="time" style={S.input} defaultValue={settings.hours_from} onBlur={(e) => e.target.value && e.target.value !== settings.hours_from && saveSettings({ hours_from: e.target.value })} />
                <span style={S.muted}>to</span>
                <input type="time" style={S.input} defaultValue={settings.hours_to} onBlur={(e) => e.target.value && e.target.value !== settings.hours_to && saveSettings({ hours_to: e.target.value })} />
                <span style={S.muted}>(India time; now {settings.now_ist}). A cashier already logged in stays logged in after closing time.</span>
              </div>
            )}
          </div>
        )}
      </section>

      <section style={S.panel}>
        <h2 style={S.h2}>Approved devices ({active.length})</h2>
        <div style={S.wrap}>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Device</th><th style={S.th}>Approved</th><th style={S.th}>Last used</th><th style={S.th}>Status</th><th style={S.th} /></tr></thead>
            <tbody>
              {devices.devices.map((d) => (
                <tr key={d.id} style={{ opacity: d.revoked ? 0.55 : 1 }}>
                  <td style={S.td}><strong>{d.name}</strong>{d.id === devices.this_device_id ? <span style={{ color: '#5b3fd9', fontWeight: 800 }}> (this device)</span> : null}</td>
                  <td style={S.td}>{when(d.created_at)}{d.approved_by ? ` by ${d.approved_by}` : ''}</td>
                  <td style={S.td}>{when(d.last_used)}{d.last_ip ? <div style={{ fontSize: 12, color: '#776e86' }}>{d.last_ip}</div> : null}</td>
                  <td style={S.td}><span style={S.pill(!d.revoked)}>{d.revoked ? 'Revoked' : 'Active'}</span></td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                    {!d.revoked && <><button type="button" style={S.link} onClick={() => rename(d)}>Rename</button>
                      <button type="button" style={{ ...S.link, color: '#d63d5b' }} onClick={() => revoke(d)}>Revoke</button></>}
                  </td>
                </tr>
              ))}
              {devices.devices.length === 0 && <tr><td colSpan={5} style={{ ...S.td, textAlign: 'center', color: '#776e86' }}>No devices approved yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section style={S.panel}>
        <div style={{ ...S.row, justifyContent: 'space-between' }}>
          <h2 style={{ ...S.h2, margin: 0 }}>Login history</h2>
          <label style={S.toggle}><input type="checkbox" checked={onlyCashier} onChange={(e) => setOnlyCashier(e.target.checked)} /> Cashier only</label>
        </div>
        <div style={S.wrap}>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Time</th><th style={S.th}>User</th><th style={S.th}>Result</th><th style={S.th}>Device</th><th style={S.th}>Browser</th><th style={S.th}>IP address</th></tr></thead>
            <tbody>
              {shownLogins.map((l) => (
                <tr key={l.id}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{when(l.created_at)}</td>
                  <td style={S.td}><strong>{l.username || '—'}</strong>{l.role ? <div style={{ fontSize: 12, color: '#776e86' }}>{l.role}</div> : null}</td>
                  <td style={S.td}><span style={S.pill(!!l.ok)}>{l.result}</span></td>
                  <td style={S.td}>{l.device_name || <span style={{ color: '#776e86' }}>Not approved</span>}</td>
                  <td style={S.td}>{browser(l.user_agent)}</td>
                  <td style={{ ...S.td, fontVariantNumeric: 'tabular-nums' }}>{l.ip || '—'}</td>
                </tr>
              ))}
              {shownLogins.length === 0 && <tr><td colSpan={6} style={{ ...S.td, textAlign: 'center', color: '#776e86' }}>No logins recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
